import { City, Country, ICity, ICountry, IState, State } from "country-state-city";
import React, { useEffect, useMemo, useState } from "react";

import * as S from "./index.styles";
import { LocationFieldConfig, LocationFieldsProps, LocationFieldType } from "./LocationFields.types";

import CitySelectField from "./CitySelectField";
import CountrySelectField from "./CountrySelectField";
import StateSelectField from "./StateSelectField";
import ZipInputField from "./ZipInputField";
import StreetInputField from "./StreetInputField";



const defaultFields: LocationFieldConfig[] = [
  { type: 'country', label: 'Country', required: true },
  { type: 'state', label: 'State/Province', required: true },
  { type: 'city', label: 'City', required: true },
  { type: 'zip', label: 'Zip Code', required: true },
  { type: 'street', label: 'Street Address', required: false },
];

interface OptionLocations {
  countries: ICountry[];
  states: IState[];
  cities: ICity[];
  zips: string[];
}

const LocationFields: React.FC<LocationFieldsProps> = ({ post_location, onChange, fields = defaultFields, border }) => {
  const countries = useMemo(() => Country.getAllCountries(), []);
  const [optionLocations, setOptionLocations] = useState<OptionLocations>({
    countries,
    states: [],
    cities: [],
    zips: [],
  });

  // Single pass: build both index and value maps
  const fieldMap = useMemo(() => {
    const idx: Partial<Record<LocationFieldType, number>> = {};
    const val: Partial<Record<LocationFieldType, string | undefined>> = {};
    fields.forEach((f) => {
      if(f.type === 'country'){
        idx[f.type] = 2;
      } else if(f.type === 'state'){
        idx[f.type] = 1;
      } else if(f.type === 'city'){
        idx[f.type] = 0;
      } else if(f.type === 'zip'){
        idx[f.type] = 3;
      } else if(f.type === 'street'){
        idx[f.type] = 4;
      }
      val[f.type] = post_location[idx[f.type]!];
    });
    return { idx, val };
  }, [fields, post_location]);

  
  // UseEffect to update the state of the location fields
  useEffect(() => {
    if (fieldMap.val.country) {
      const selectedCountry = countries.find(c => c.name === fieldMap.val.country);
      const stateList = selectedCountry ? State.getStatesOfCountry(selectedCountry.isoCode) : [];
      setOptionLocations(prev => ({ ...prev, states: stateList }));
      if (stateList.length === 0 && fieldMap.idx.city !== undefined && fieldMap.val.country) {
        setOptionLocations(prev => ({ ...prev, cities: selectedCountry ? City.getCitiesOfCountry(selectedCountry.isoCode) || [] : [] }));
      } else {
        setOptionLocations(prev => ({ ...prev, cities: [] }));
      }
      const fieldTypes = fields.map(f => f.type).filter(type => type !== 'country');
      fieldTypes.forEach(type => {
        if (fieldMap.idx[type] !== undefined) onChange(fieldMap.idx[type]!, "");
      });
    } else {
      setOptionLocations(prev => ({ ...prev, states: [], cities: [] }));
    }
  }, [fieldMap.val.country]);

  useEffect(() => {
    if (fieldMap.val.country && fieldMap.val.state) {
      const selectedCountry = countries.find(c => c.name === fieldMap.val.country);
      const selectedState = optionLocations.states.find(s => s.name === fieldMap.val.state);
      const cityList = (selectedCountry && selectedState)
        ? City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode) || []
        : [];
      setOptionLocations(prev => ({ ...prev, cities: cityList }));
      (['city', 'zip'] as LocationFieldType[]).forEach(type => {
        if (fieldMap.idx[type] !== undefined) onChange(fieldMap.idx[type]!, "");
      });
    }
  }, [fieldMap.val.state]);

  useEffect(() => {
    if (fieldMap.idx.zip !== undefined && fieldMap.idx.city !== undefined) {
      onChange(fieldMap.idx.zip, "");
    }
  }, [fieldMap.val.city]);

  // Render
  return (
    <S.LocationArea border={border}>
      <S.LocationFormRow>
        {fields.map((field) => {
          const label = field.label;
          const required = field.required;
          const idx = fieldMap.idx[field.type]!;
          const value = fieldMap.val[field.type];

          if (field.type === "country") {
            return (
              <CountrySelectField
                key={field.type}
                value={value}
                options={countries}
                onChange={val => onChange(idx, val)}
                label={label}
                required={required}
                border={border}
              />
            );
          }
          if (field.type === "state") {
            if (!fieldMap.val.country || optionLocations.states.length === 0) return null;
            return (
              <StateSelectField
                key={field.type}
                value={value}
                options={optionLocations.states}
                onChange={val => onChange(idx, val)}
                label={label}
                required={required}
                border={border}
              />
            );
          }
          if (field.type === "city") {
            if (!fieldMap.val.country || !fieldMap.val.state || optionLocations.cities.length === 0) return null;
            return (
              <CitySelectField
                key={field.type}
                value={value}
                options={optionLocations.cities}
                onChange={val => onChange(idx, val)}
                label={label}
                required={required}
                border={border}
              />
            );
          }
          if (field.type === "zip") {
            if (optionLocations.cities.length === 0) return null;
            return (
              <ZipInputField
                key={field.type}
                value={value}
                onChange={val => onChange(idx, val)}
                label={label}
                required={required}
                border={border}
              />
            );
          }
          if (field.type === "street") {
            return (
              <StreetInputField
                key={field.type}
                value={value}
                onChange={val => onChange(idx, val)}
                label={label}
                required={required}
                border={border}
              />
            );
          }
          return null;
        })}
      </S.LocationFormRow>
    </S.LocationArea>
  );
};

export default LocationFields; 